<template>
  <div class="container-fluid p-0">
    <div class="row g-0">
      <!-- Left side - Registration Form -->
      <div class="col-md-6 d-flex flex-column align-items-center justify-content-center bg-white px-4">
        <div class="card border-0 shadow-s w-100 mx-auto my-3 mx-3">
          <div class="card-body py-4">
            <!-- Logo -->
            <div class="mb-4">
              <div class="d-flex align-items-center">
               <img :src="brandLogo" :alt="brandName" class="mb-3" width="120"/>
              </div>
            </div>

            <!-- Form Heading -->
            <h2 class="fw-bold mb-4">Create an Account</h2>

            <!-- General Error Message -->
            <div v-if="generalError" class="alert alert-danger mb-3">
              {{ generalError }}
            </div>

            <!-- Registration Form -->
            <div class="mx-3">
              <!-- Full Name -->
              <div class="mb-3">
                <label for="fullname" class="form-label small fw-medium">Fullname*</label>
                <ArgonInput type="text" v-model="fullname" placeholder="Enter your fullname" required/>
                <div v-if="errors.fullname" class="text-danger small mt-1">
                  {{ errors.fullname[0] }}
                </div>
              </div>

              <!-- Email -->
              <div class="mb-3">
                <label for="email" class="form-label small fw-medium">Email Address*</label>
                <ArgonInput
                  type="email"
                  id="email"
                  v-model="email"
                  placeholder="Email Address"
                  required
                />
                <div v-if="errors.email" class="text-danger small">
                  {{ errors.email[0] }}
                </div>
              </div>

              <!-- Company Name -->
              <div class="mb-3">
                <label for="company" class="form-label small fw-medium">Company Name*</label>
                <ArgonInput
                    type="text"
                    v-model="company_name"
                    id="company"
                    placeholder="Company Name"
                    required
                  />
                <div v-if="errors.company_name" class="text-danger small">
                  {{ errors.company_name[0] }}
                </div>
              </div>

              <div class="mb-3">
                <label for="phone" class="form-label small fw-medium">Phone Number*</label>
                <ArgonInput
                    type="text"
                    v-model="phone"
                    id="phone"
                    placeholder="Phone Number"
                    required
                  />
                <div v-if="errors.phone" class="text-danger small">
                  {{ errors.phone[0] }}
                </div>
              </div>

              <!-- Create Password -->
              <div class="mb-3">
                <label for="password" class="form-label small fw-medium">Create Password*</label>
                <div class="input-group">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    id="password"
                    v-model="password"
                    placeholder="Create Strong Password"
                    required
                  >
                  <button
                    class="input-group-text bg-transparent border-start-0"
                    type="button"
                    @click="togglePasswordVisibility('password')"
                  >
                    <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye text-secondary" viewBox="0 0 16 16">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye-slash text-secondary" viewBox="0 0 16 16">
                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                    </svg>
                  </button>
                </div>
                <div v-if="errors.password" class="text-danger small">
                  {{ errors.password[0] }}
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="mb-4">
                <label for="confirm-password" class="form-label small fw-medium">Confirm Password*</label>
                <div class="input-group">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    id="confirm-password"
                    v-model="confirm_password"
                    placeholder="Confirm password"
                    required
                  >
                  <button
                    class="input-group-text bg-transparent border-start-0"
                    type="button"
                    @click="togglePasswordVisibility('confirmPassword')"
                  >
                    <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye text-secondary" viewBox="0 0 16 16">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye-slash text-secondary" viewBox="0 0 16 16">
                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                    </svg>
                  </button>
                </div>
                <div v-if="errors.password_confirmation" class="text-danger small">
                  {{ errors.password_confirmation[0] }}
                </div>
              </div>

               <!-- Terms & Conditions Checkbox -->
              <div class="mb-3">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="agreeTerms" 
                    v-model="agreedToTerms"
                  >
                  <label class="form-check-label small" for="agreeTerms">
                    I agree to the 
                    <a href="#" @click.prevent="openTermsModal" class="fw-bold text-decoration-none" style="color: #28a745 !important;">Terms & Conditions</a> 
                    and 
                    <a href="#" @click.prevent="openPrivacyModal" class="fw-bold text-decoration-none" style="color: #28a745 !important;">Privacy Policy</a>*
                  </label>
                </div>
                <div v-if="errors.agreedToTerms" class="text-danger small mt-1">
                  {{ errors.agreedToTerms[0] }}
                </div>
              </div>

              <!-- Submit Button -->
              <div class="d-grid gap-2 mb-3">
                <argon-button @click="submitForm" class="btn btn-success btn-lg" :disabled="!agreedToTerms">Register</argon-button>
              </div>

              <!-- Login Link -->
              <div class="text-center mb-3">
                <small>Already have an account? <a href="/signin" class="text-success text-decoration-none fw-medium">Login Here</a></small>
              </div>
            </div>
          </div>
        </div>

        <!-- Left column footer - moved outside card but still within the left column, with gray background -->
        <!-- <div class="w-100 py-3 mt-auto">
          <div class="text-center">
            <small class="text-muted">
              © PT Anugrah Multi Remittance 2025 | <a href="#" class="text-muted text-decoration-none">Term & Conditions</a> | <a href="#" class="text-muted text-decoration-none">Privacy Policy</a>
            </small>
          </div>
        </div> -->
      </div>

      <!-- Right side - Modified to use direct IMG tag -->
      <div class="col-md-6 d-none d-md-block position-relative overflow-hidden" style="min-height: 100vh;">
        <!-- Using a direct img tag with overlay -->
        <div class="position-relative h-100">
          <img src="../../../public/register-image.png" alt="Registration Background"
               class="w-100 h-100 position-absolute" style="object-fit: cover;" />
        </div>
      </div>
    </div>
  </div>

  <!-- Terms & Conditions Modal -->
  <div v-if="showTermsModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showTermsModal = false">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="termsModalLabel">Terms & Conditions</h5>
          <button type="button" class="btn-close" @click="showTermsModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <h6 class="fw-bold">Domestics Fund Transfer Transaction Terms and Conditions</h6>
          <p>Customers (referring to individual and corporate customers) engage in transactions related to Domestics fund transfers with PT Anugrah Multi Remmitance (hereinafter referred to as "Swiftrans") after agreeing to the contents of these Domestics Fund Transfer Transaction Terms and Conditions (hereinafter referred to as "Terms and Conditions") set by Swiftrans, and the Terms and Conditions constitute a binding contract between the Customer and Swiftrans.</p>
          
          <h6 class="fw-bold mt-4">1. General Terms</h6>
          <p><strong>I. Purpose</strong></p>
          <p>The purpose of these Terms and Conditions is to establish the rights and obligations between Swiftrans and Customers who wish to use the Services provided by Swiftrans and to set the terms for the use of the Services.</p>
          
          <p><strong>II. Definitions</strong></p>
          <p>In these Terms and Conditions, the following terms have the meanings as set forth below:</p>
          <p>1) "Service" refers to the services provided by Swiftrans for transferring funds for Users, both Domesticsly and domestically.</p>
          <p>2) "User" means the customer authorized by Swiftrans to use the Service in accordance with the procedure specified by Swiftrans.</p>
          <p>3) "Correspondent" refers to Swiftrans's partner and/or its business partners located abroad that process fund transfers.</p>
          <p>4) "Internet Fund Transfer" means Domestics fund transfers using the Swiftrans website.</p>
          <p>5) "Login Password" means the code used to access the use of the Service via the Mobile Application or the Internet, created by changing the temporary login password received from Swiftrans.</p>
          <p>6) "Transaction Password" means the code used when conducting transactions using the Swiftrans Service.</p>
          
          <p><strong>III. Use of the Service</strong></p>
          <p>In using the Service, the Customer must complete the registration procedure in accordance with Swiftrans's terms and conditions.</p>
          <p>1) The Customer will be considered to have agreed to these Terms and Conditions.</p>
          <p>2) In principle, the Customer can, in accordance with these Terms and Conditions, use the Service 24 (twenty-four) hours a day, every day throughout the year by downloading and using the Swiftrans Application.</p>
          <p>3) Regardless of the above provisions, Swiftrans may suspend and/or terminate the provision of all or part of the Service for system maintenance or other purposes required for better Service provision.</p>
          <p>4) If Swiftrans suspends the Service temporarily, Swiftrans will provide prior notice to the Customer; however, provided that, if there is an urgent need by Swiftrans due to system failure, etc., Swiftrans may suspend and/or terminate all or part of the system without prior notice.</p>
          
          <h6 class="fw-bold mt-4">2. Registration</h6>
          <p><strong>I. Registration Procedure</strong></p>
          <p>1) When applying to use the Service, the Customer is required to complete the form provided by Swiftrans. The Customer is fully responsible for the accuracy of the data provided in the form and releases Swiftrans from any legal claims that may arise in the future due to errors in the data provided, whether intentional or not.</p>
          <p>2) Swiftrans will conduct a Know Your Customer (KYC) process in accordance with the applicable laws and regulations. Swiftrans may appoint a third party to conduct the KYC procedure.</p>
          <p>3) In the event that a User's account is used by someone else, the User must attach a letter of authorization to the party appointed to conduct the transaction. Swiftrans is not responsible for any transactions carried out by unauthorized parties.</p>
          <p>4) In the event that a Customer does not pass the KYC procedure, Swiftrans has the right to terminate the Customer's registration process, which will result in the Customer being unable to use the Service. Swiftrans's decision is final and cannot be disputed.</p>
          <p>5) Customers are not allowed to:</p>
          <p>a) provide false information;</p>
          <p>b) Use a user's email address with the intention of impersonating that person;</p>
          <p>c) Use another user's email without authorization or permission from the email owner; and</p>
          <p>d) Use or link financial instruments owned by someone other than the Customer into the Swiftrans Application.</p>
          <p>Swiftrans has the right to suspend or terminate a User's account if inaccurate information is found. The decision to suspend and/or terminate an account is at the discretion of Swiftrans and is final and cannot be challenged.</p>
          
          <p><strong>II. Account Upgrade</strong></p>
          <p>Users will be required to upgrade their accounts by providing additional information to fully utilize the features of the Service.</p>
          <p>If a User decides not to upgrade their account, they will not be able to fully enjoy Swiftrans's services.</p>
          
          <p><strong>III. Data Changes</strong></p>
          <p>In the event of changes in a Customer's data, the Customer is required to update the data on the dashboard as provided by Swiftrans.</p>
          <p>Swiftrans will freeze a User's account during the KYC process for the latest User data.</p>
          
          <h6 class="fw-bold mt-4">3. Prohibited Activities and Anti-Money Laundering and Counterterrorism Financing Terms</h6>
          <p>1) Users hereby agree that they will not engage in any of the following when using or accessing the Service:</p>
          <p>a) attempting to access or search the Service or download content from the Service through the use of machinery, software, tools, agents, devices, or mechanisms (including but not limited to robots, crawlers, data mining tools, or the like) other than software and/or agents provided by Swiftrans or generally available on web browsers.</p>
          <p>b) Accessing, modifying, interfering with or using non-public areas of the Service, our computer systems, or the technical delivery systems of our providers, including but not limited to uploading or distributing viruses, adware, spyware, worms, or other harmful code.</p>
          <p>c) Collecting and using information, such as the usernames of other users, real names, email addresses available on the Service, and using them to send unsolicited advertising, junk mail, spam, or other unwanted forms.</p>
          <p>d) using the Service for commercial purposes or for the benefit of third parties or in a manner that contradicts the Terms and Conditions.</p>
          <p>e) violating applicable laws or regulations; or</p>
          <p>f) encouraging or enabling others to do any of the above; or</p>
          <p>g) interfering with the comfort of other users in using the Service, including without limitation, by</p>
          <p>i. making unsolicited offers or advertisements to other users of the Service or</p>
          <p>ii. attempting to collect personal information about other users or third parties without their consent. Swiftrans has the right to investigate and bring to court violations of any of the above, and/or engage and cooperate with law enforcement authorities in prosecuting Users who violate the Terms and Conditions.</p>
          <p>Swiftrans is an entity that enforces anti-money laundering and counterterrorism financing provisions ("APU-PPT"). In providing the Service, Swiftrans may take any actions allowed by law to enforce APU-PPT provisions. By agreeing to these Terms and Conditions, Users consent to Swiftrans taking actions related to APU-PPT enforcement.</p>
          
          <h6 class="fw-bold mt-4">4. Transfer Limits</h6>
          <p>In conducting fund transfers, Swiftrans will apply maximum transfer limits in accordance with the applicable rules.</p>
          
          <h6 class="fw-bold mt-4">5. Changes to the Terms and Conditions</h6>
          <p>Swiftrans has the right to change the Terms and Conditions at any time. Please check the Terms and Conditions regularly to see if there have been any changes.</p>
          <p>In the event of changes to the Terms and Conditions that affect Users' rights and obligations, Swiftrans will make efforts to notify Users of such changes.</p>
          <p>Swiftrans may provide notifications through in-app messages, pop-up messages, or banners within the Service, by sending emails to the address used by Users to register an account, or through similar mechanisms.</p>
          <p>In addition, if changes to the Terms and Conditions alter Users' rights and obligations, Swiftrans may require Users to re-consent to accepting the changed Terms and Conditions.</p>
          <p>If Swiftrans requires Users' consent for changes to the Terms and Conditions, the effects of the changes will only apply after Users accept the changed Terms and Conditions.</p>
          <p>If Users do not accept the changes to the Terms and Conditions, Users can inform Swiftrans to discontinue Users' access to the Service.</p>
          <p>All other changes will apply after the updated Terms and Conditions are published. Disputes arising from the Terms and Conditions will be resolved in accordance with the Terms and Conditions in effect at the time the dispute arises.</p>
          
          <h6 class="fw-bold mt-4">6. Ownership</h6>
          <p>Swiftrans Services (including but not limited to the Application) are protected by copyright, trademarks, and other laws of the Republic of Indonesia.</p>
          <p>Except as expressly provided in the Terms and Conditions, Swiftrans (or its licensors) exclusively owns all rights, ownership, and interests in and to the Services (including but not limited to the Application), including all related intellectual property rights.</p>
          <p>Except as expressly provided in the Terms and Conditions, Customers are not permitted to use the Services, and Swiftrans retains all rights to the Services. Customers may not delete, alter, or obscure any copyright, trademark, service mark, or ownership notices incorporated in or accompanying the Services (including but not limited to the Application).</p>
          <p>Users acknowledge and agree that any feedback, comments, or suggestions provided by Users regarding the Services (including but not limited to the Application) ("Feedback") will be the sole and exclusive property of Swiftrans, and hereby Customers grant rights to all such Feedback.</p>
          
          <h6 class="fw-bold mt-4">7. Disclaimer</h6>
          <p>Customers understand and agree that the Services (including but not limited to the content or material offered through the Service) are provided to Customers on an "as is" and "as available" basis. Without limiting the foregoing, Swiftrans explicitly disclaims all warranties, whether express, implied, or otherwise, including but not limited to the warranties of merchantability, fitness for a particular purpose, non-infringement, and any warranties arising out of the course of dealing or usage of trade.</p>
          <p>Swiftrans makes no warranty that the Service (including but not limited to the content or material obtained from the Service) will meet Customers' needs or be available in an uninterrupted, error-free, or free of harmful components.</p>
          <p>Customers are responsible for the risks associated with the use or access to the Service (including but not limited to the content or material obtained from the Service) and transactions with other service users, and for all damages that may result from such use or access.</p>
          
          <h6 class="fw-bold mt-4">8. Limitation of Liability</h6>
          <p>Swiftrans's liability is limited to the actual value of the loss directly caused by Swiftrans. Swiftrans shall not be liable to the user for any indirect, incidental, special, punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from (I) the user's access to or inability to access or use the service (including but not limited to limitations on content obtained from the service); (II) any actions or content of any third party service; or (III) unauthorized access, use or alteration of transmissions or content.</p>
          
          <h6 class="fw-bold mt-4">9. Dispute Resolution and Arbitration</h6>
          <p>Customers and Swiftrans agree that any and all disputes arising in connection with the Terms and Conditions must be resolved through arbitration. Arbitration uses a neutral arbitrator and not a judge or jury, allowing for more limited discovery than in court.</p>
          <p>In most cases, the arbitrator can award the same damages as a court. The agreement between Swiftrans and Customers for arbitration of disputes includes, but is not limited to, all claims arising from or relating to any aspect of the Terms and Conditions, whether based in contract, tort, statute, fraud, misrepresentation, or any other legal theory, and regardless of whether the claim arises during or after the Terms and Conditions have terminated.</p>
          <p>Any resolution between Customers and Swiftrans will be governed by the National Arbitration Board of Indonesia ("BANI"), which is recognized by the judicial institutions in Indonesia, and will be conducted in accordance with the rules applicable to BANI.</p>
          <p>Arbitration hearings will be held in Jakarta, and the arbitrator's decision, including decisions on damages, arbitration costs, and legal fees of all parties, as well as other costs, will be final and binding.</p>
          
          <h6 class="fw-bold mt-4">10. Questions and Contact Information</h6>
          <p>This service is provided by PT Anugrah Multi Remmitance, addressed at Komplek Bumi Indah Blok F No 2, Lubuk Baja Kota, Kecamatan Lubuk Baja, Kota Batam. Please contact Swiftrans if the User has any questions regarding these Terms and Conditions. The User can contact Swiftrans by sending a letter to the above address or by sending an email to cs@swiftrans.id.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showTermsModal = false">Close</button>
          <button type="button" class="btn btn-success" @click="acceptTerms">I Agree</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Privacy Policy Modal -->
  <div v-if="showPrivacyModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showPrivacyModal = false">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
                <div class="modal-header">
          <h5 class="modal-title" id="privacyModalLabel">Privacy Policy</h5>
          <button type="button" class="btn-close" @click="showPrivacyModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="fw-bold mb-3">Last Updated: July 21, 2025</p>
          
          <h6 class="fw-bold mt-4">1. Introduction</h6>
          <p>PT Anugrah Multi Remittance, hereinafter referred to as "Swiftrans", respects and is fully committed to protecting the privacy of our Users. This Privacy Policy explains how we collect, store, use, disclose, and protect personal data obtained through the use of Swiftrans' international remittance services.</p>
          <p>By using Swiftrans' services, the User is deemed to have read, understood, and agreed to the terms of this Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">2. Personal Data Collected</h6>
          <p>Swiftrans may collect the following types of information:</p>
          <p><strong>Identity Information:</strong> Full name, identification number (ID/Passport), date of birth, nationality.</p>
          <p><strong>Contact Information:</strong> Address, phone number, and email address.</p>
          <p><strong>Transaction Information:</strong> Amount and destination of the transfer, recipient's name, bank account details.</p>
          <p><strong>Financial Information:</strong> Proof of source of funds, bank account used.</p>
          <p><strong>Technical Information:</strong> IP address, activity logs, device and application data.</p>
          <p><strong>Other Information</strong> required in accordance with laws and regulations on Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF).</p>
          
          <h6 class="fw-bold mt-4">3. Purpose of Data Collection</h6>
          <p>The personal data collected will be used for the following purposes:</p>
          <p>• To provide, manage, and improve Swiftrans' services.</p>
          <p>• To carry out identity verification and Know Your Customer (KYC) processes.</p>
          <p>• To comply with applicable laws and regulations (including AML-CTF).</p>
          <p>• To detect and prevent fraud, misuse, or other illegal activities.</p>
          <p>• To provide information to competent authorities when legally required.</p>
          <p>• To communicate important updates regarding transactions or services.</p>
          
          <h6 class="fw-bold mt-4">4. Legal Basis for Data Processing</h6>
          <p>We process personal data based on the following legal grounds:</p>
          <p>• The User's consent.</p>
          <p>• Legal obligations (including regulations from Bank Indonesia, PPATK, and other applicable laws).</p>
          <p>• Legitimate interests of the company in ensuring security and operational efficiency.</p>
          <p>• Contractual obligations between Swiftrans and the User.</p>
          
          <h6 class="fw-bold mt-4">5. Data Storage and Protection</h6>
          <p>• Data is stored on servers protected both physically and digitally, in accordance with industry standards.</p>
          <p>• Swiftrans implements encryption, firewalls, and access controls to prevent unauthorized access.</p>
          <p>• Data will be retained as long as necessary for processing purposes or as permitted by law.</p>
          
          <h6 class="fw-bold mt-4">6. Sharing of Personal Data</h6>
          <p>Swiftrans may share personal data with the following third parties:</p>
          <p>• Overseas correspondent partners for international remittance purposes.</p>
          <p>• Domestic correspondent partners for domestic fund transfers.</p>
          <p>• Verification and KYC service providers.</p>
          <p>• Regulatory and legal authorities (Bank Indonesia, PPATK, Police, etc.).</p>
          <p>All third parties are required to adhere to data protection principles equivalent to our own.</p>
          
          <h6 class="fw-bold mt-4">7. User Rights Regarding Personal Data</h6>
          <p>Users have the right to:</p>
          <p>• Access and obtain a copy of their personal data.</p>
          <p>• Update or correct inaccurate personal data.</p>
          <p>• Withdraw consent (with certain consequences on the services).</p>
          <p>• Request deletion of personal data, in accordance with applicable laws and regulations.</p>
          <p>Requests regarding these rights may be submitted via: <strong>privacy@swiftrans.co.id</strong></p>
          
          <h6 class="fw-bold mt-4">8. Cookies and Similar Technologies</h6>
          <p>We use cookies and similar technologies to:</p>
          <p>• Enhance user experience.</p>
          <p>• Collect data on service usage.</p>
          <p>• Provide more relevant content.</p>
          <p>Users can manage their cookie preferences through their respective browser settings.</p>
          
          <h6 class="fw-bold mt-4">9. Changes to the Privacy Policy</h6>
          <p>Swiftrans may update this Privacy Policy from time to time. We will notify Users of any material changes via the application, email, or other official communication channels.</p>
          <p>Continued use of the services following any changes will constitute acceptance of the updated Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">10. Contact Information</h6>
          <p>For any questions, information requests, or complaints regarding this Privacy Policy, please contact us at:</p>
          <p><strong>PT Anugrah Multi Remittance (Swiftrans)</strong></p>
          <p><strong>Office Address:</strong> Komplek Bumi Indah Blok F No. 2, Lubuk Baja Kota, Lubuk Baja District, Batam City</p>
          <p><strong>Email:</strong> cs@swiftrans.co.id</p>
          
          <h6 class="fw-bold mt-4">11. Governing Law</h6>
          <p>This Policy shall be governed by and construed in accordance with the laws of the Republic of Indonesia, including Law Number 27 of 2022 on Personal Data Protection, and its implementing regulations.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showPrivacyModal = false">Close</button>
          <button type="button" class="btn btn-success" @click="acceptPrivacy">I Understand</button>
        </div>
      </div>
    </div>
  </div>

  <Loading :isLoading="isLoading" />
</template>

<script>
import { onBeforeUnmount, onBeforeMount } from "vue";
import { useStore } from "vuex";
import ArgonInput from "@/components/ArgonInput";
import ArgonButton from "@/components/ArgonButton";
import { buildFormData } from "../../utils/param-builder";
import { registerAccount } from "../../api/auth";
import Loading from "@/components/Loading";
import { useBranding } from "@/utils/branding";

export default {
  components: {
    ArgonInput,
    ArgonButton,
    Loading
  },
  data() {
    const { brandLogo, brandName } = useBranding();
    return {
      fullname: '',
      email: '',
      phone: '',
      company_name: '',
      password: '',
      confirm_password: '',
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      errors: {},
      generalError: '',
      agreedToTerms: false,
      showTermsModal: false,
      showPrivacyModal: false,
      brandLogo,
      brandName,
    };
  },
  methods: {
    clearErrors() {
      this.errors = {};
      this.generalError = '';
    },
    async submitForm() {
      this.clearErrors();
      if (!this.validate()) {
        return;
      }

      this.isLoading = true;

      const params = {
        fullname: this.fullname,
        email: this.email,
        company_name: this.company_name,
        phone: this.phone,
        password: this.password,
        password_confirmation: this.confirm_password,
      };

      try {
        const formData = buildFormData(params);
        const response = await registerAccount(formData);

        if (response.status === "success") {
          this.$store.commit("auth/setEmail", this.email);
          this.$router.push({ name: "VerifyEmail" });
        } else {
          this.generalError = response.message || "Registration failed";
        }
      } catch (error) {
        if (error.response) {
          const errorData = error.response.data;

          // Handle validation errors (422)
          if (error.response.status === 422 && errorData.data) {
            this.errors = errorData.data;
            this.generalError = errorData.message || 'Please correct the errors below.';
          } else {
            // Handle other error responses
            this.generalError = errorData.message || "Registration failed. Please try again.";
          }
        } else {
          this.generalError = "An unexpected error occurred. Please try again.";
        }
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.fullname = "";
      this.email = "";
      this.phone = "";
      this.company_name = "";
      this.password = "";
      this.confirm_password = "";
      this.agreedToTerms = false;
    },
    validate() {
      let isValid = true;
      this.clearErrors();

      // Fullname validation
      if (!this.fullname) {
        this.errors.fullname = ["Fullname is required"];
        isValid = false;
      } else if (this.fullname.length < 2 || this.fullname.length > 100) {
        this.errors.fullname = ["Fullname must be between 2 and 100 characters"];
        isValid = false;
      }

      // Email validation
      if (!this.email) {
        this.errors.email = ["Email is required"];
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.errors.email = ["Please enter a valid email address"];
          isValid = false;
        }
      }

      // Company name validation
      if (!this.company_name) {
        this.errors.company_name = ["Company name is required"];
        isValid = false;
      } else if (this.company_name.length < 2 || this.company_name.length > 100) {
        this.errors.company_name = ["Company name must be between 2 and 100 characters"];
        isValid = false;
      }

      // Phone validation
      if (!this.phone) {
        this.errors.phone = ["Phone number is required"];
        isValid = false;
      }

      // Password validation
      if (!this.password) {
        this.errors.password = ["Password is required"];
        isValid = false;
      } else if (this.password.length < 8) {
        this.errors.password = ["Password must be at least 8 characters long"];
        isValid = false;
      } else {
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=\-[\]{};:"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+=\-[\]{};:"\\|,.<>/?]{8,}$/;
        if (!passwordRegex.test(this.password)) {
          this.errors.password = ["Password must contain at least one uppercase letter, one number, and one special character"];
          isValid = false;
        }
      }

      // Confirm password validation
      if (!this.confirm_password) {
        this.errors.password_confirmation = ["Confirm password is required"];
        isValid = false;
      } else if (this.password !== this.confirm_password) {
        this.errors.password_confirmation = ["Passwords do not match"];
        isValid = false;
      }

      // Terms & Conditions validation
      if (!this.agreedToTerms) {
        this.errors.agreedToTerms = ["You must agree to the Terms & Conditions and Privacy Policy"];
        isValid = false;
      }

      return isValid;
    },
    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword;
      } else if (field === 'confirmPassword') {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    },
    openTermsModal() {
      this.showTermsModal = true;
    },
    openPrivacyModal() {
      this.showPrivacyModal = true;
    },
    acceptTerms() {
      this.agreedToTerms = true;
      this.showTermsModal = false;
      // Clear any existing error for terms
      if (this.errors.agreedToTerms) {
        delete this.errors.agreedToTerms;
      }
    },
    acceptPrivacy() {
      this.showPrivacyModal = false;
    },
  },
  setup() {
    const store = useStore();
    const body = document.getElementsByTagName("body")[0];

    onBeforeMount(() => {
      store.state.hideConfigButton = true;
      store.state.showNavbar = false;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showNavbar = true;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });
  },
};
</script>
